import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { CTAButton } from "./CTAButton";
import { NAV, EASE } from "@/lib/site";
import { scrollToId, goToContact } from "@/lib/scroll";

const MobileMenu = ({ onNav }) => (
  <motion.div
    data-testid="mobile-menu"
    initial={{ clipPath: "inset(0 0 100% 0)" }}
    animate={{ clipPath: "inset(0 0 0% 0)" }}
    exit={{ clipPath: "inset(0 0 100% 0)" }}
    transition={{ duration: 0.6, ease: EASE }}
    className="fixed inset-0 z-40 flex flex-col bg-ws-bg/[0.98] px-6 pb-10 pt-28 lg:hidden"
  >
    <div className="dot-grid mask-radial pointer-events-none absolute inset-0 opacity-60" />
    <nav className="relative flex flex-col gap-1">
      {NAV.map((item, i) => (
        <motion.button
          key={item.id}
          data-testid={`mobile-nav-${item.id}`}
          onClick={() => onNav(item.id)}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 + i * 0.05, duration: 0.5, ease: EASE }}
          className="flex items-baseline gap-4 border-b border-white/5 py-4 text-left font-display text-2xl text-ws-text"
        >
          <span className="font-mono text-xs text-ws-lime">0{i + 1}</span>
          {item.label}
        </motion.button>
      ))}
    </nav>
    <CTAButton testid="mobile-menu-cta" className="relative mt-auto w-full" onClick={() => onNav("contato")}>
      Solicitar orçamento
    </CTAButton>
  </motion.div>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 250 : 0);
  };

  return (
    <>
      <motion.header
        data-testid="site-header"
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
        className="fixed inset-x-0 top-0 z-50 px-3 pt-3 sm:px-6 sm:pt-4"
      >
        <div
          className={`mx-auto flex max-w-7xl items-center justify-between rounded-full px-4 py-2.5 transition-[background-color,border-color,box-shadow] duration-500 sm:px-6 ${
            scrolled || open
              ? "glass shadow-[0_10px_40px_-15px_rgba(0,0,0,.9)]"
              : "border border-transparent bg-transparent"
          }`}
        >
          <button data-testid="header-logo" onClick={() => handleNav("inicio")} aria-label="WebStation — início">
            <Logo />
          </button>
          <nav className="hidden items-center gap-1 lg:flex" aria-label="Navegação principal">
            {NAV.map((item) => (
              <button
                key={item.id}
                data-testid={`nav-${item.id}`}
                onClick={() => handleNav(item.id)}
                className="group relative rounded-full px-3 py-2 text-[13px] font-medium text-ws-muted transition-colors duration-300 hover:text-ws-text xl:px-3.5"
              >
                {item.label}
                <span className="absolute inset-x-3 -bottom-0.5 h-px origin-left scale-x-0 bg-ws-lime transition-transform duration-300 group-hover:scale-x-100" />
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <CTAButton testid="header-cta" className="hidden !px-5 !py-2.5 sm:inline-flex" onClick={() => goToContact()}>
              Solicitar orçamento
            </CTAButton>
            <button
              data-testid="mobile-menu-toggle"
              aria-label={open ? "Fechar menu" : "Abrir menu"}
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
              className="grid h-11 w-11 place-items-center rounded-full border border-white/10 bg-white/[0.04] text-ws-text transition-colors hover:border-ws-lime/60 lg:hidden"
            >
              <motion.span key={open ? "x" : "m"} initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }}>
                {open ? <X size={20} /> : <Menu size={20} />}
              </motion.span>
            </button>
          </div>
        </div>
      </motion.header>
      <AnimatePresence>{open && <MobileMenu onNav={handleNav} />}</AnimatePresence>
    </>
  );
};
